import React from 'react';
import StaticAST from '../../../images/SAST.png';
import DynamicAST from '../../../images/DAST.png';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from '@mui/material';
import { useLocation } from "react-router-dom";

const SecurityTestPlan = ({ securityTestPlanResponse }) => {
    const location = useLocation();
    const securityTestPlan = securityTestPlanResponse;
    const clientName = securityTestPlan.securityTestPlan.clientName;
    const applicationName = securityTestPlan.securityTestPlan.applicationName;
    const applicationURL = securityTestPlan.securityTestPlan.applicationURL;



    const CustomBulletList = ({ children }) => {
        const listItems = React.Children.toArray(children).map((child, index) => {
            if (child.type === 'li') {
                return (
                    <li
                        key={index}
                        style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '0.5rem',
                            marginBottom: '0.5rem'
                        }}
                    >
                        <ArrowRightIcon fontSize="small" />
                        <span>{child.props.children}</span>
                    </li>
                );
            }
            return child;
        });

        return (
            <ul style={{ listStyle: 'none', padding: 0 }}>
                {listItems}
            </ul>
        );
    };

    const CustomTable = ({ headers, rows }) => {
        return (
            <Table
                size="small"
                sx={{
                    border: "1px solid rgba(224, 224, 224, 1)",
                    borderCollapse: "collapse",
                    mt: 1,
                    mb: 1
                }}
            >
                <TableHead>
                    <TableRow sx={{ backgroundColor: "#f5f5f6" }}>
                        {headers.map((header, index) => (
                            <TableCell
                                key={index}
                                sx={{ border: "1px solid rgba(224, 224, 224, 1)", fontSize: "0.90rem" }}
                            >
                                <Typography fontWeight="bold" fontSize="0.90rem">{header}</Typography>
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row, rowIndex) => (
                        <TableRow key={rowIndex}>
                            {row.map((cell, colIndex) => (
                                <TableCell
                                    key={colIndex}
                                    sx={{ border: "1px solid rgba(224, 224, 224, 1)", fontSize: "0.90rem" }}
                                >
                                    {cell}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        );
    };

    const headers1 = ["Target", "Description of Activity", "Tools"];
    const rows1 = [
        [`${clientName}'s ${applicationName}`, "DAST/VAPT", "XSS Finder, Reporting Metrics Measurement Framework, Broken Authentication Scanner, Automated IDOR Detection, QualiSec and Burp Suite."],
        [`${clientName}'s ${applicationName}`, "SAST", "SonarQube and Semgrep."]
    ];


    const headers2 = ["Targets", "Application URL"];
    const rows2 = [
        [`${clientName}'s ${applicationName}`, `${applicationURL}`]
    ];

    const headers3 = ["Application Name", "Description"];
    const rows3 = [
        ["None", "None"]
    ];

    const headers4 = ["S.No.", "TEST IDENTIFIER", "TEST NAME"];
    const rows4 = [
        ["1", "A-01", "Broken Access Control"],
        ["2", "A-02", "Cryptographic Failures"],
        ["3", "A-03", "Injection"],
        ["4", "A-04", "Insecure Design"],
        ["5", "A-05", "Security Misconfiguration"],
        ["6", "A-06", "Vulnerable and Outdated Components"],
        ["7", "A-07", "Identification and Authentication Failures"],
        ["8", "A-08", "Software and Data Integrity Failures"],
        ["9", "A-09", "Security Logging and Monitoring Failures"],
        ["10", "A-010", "Server-Side Request Forgery"],
    ]

    const sastTestCaseHeaders = ["Functional Module", "Test Case", "Description"];
    const sastTestCaseRows = securityTestPlan?.sastTestCases?.map(item => [
        item["Functional Module"],
        item["Test Case"],
        item["Description"]
    ]);

    const dastTestCaseHeaders = ["Functional Module", "Test Case", "Description"];
    const dastTestCaseRows = securityTestPlan?.dastTestCases?.map(item => [
        item["Functional Module"],
        item["Test Case"],
        item["Description"]
    ]);

    const sastHeaders = ["S.No.", "Phase", "Estimated Days"];
    const sastRows = securityTestPlan?.securityTestPlan?.SASTSchedule?.map(item => [
        item["S.No."],
        item["Phase"],
        item["Estimated Days"]
    ]);

    const dastHeaders = ["Phase", "Estimated Days"]
    const dastRows = [[[
        "1. Information Gathering",
        "2. Planning & Analysis",
        "3. Vulnerability Detection",
        "4. Penetration Tests & Attacks",
        "5. Reporting"
    ].map((item, idx) => <div key={idx}>{item}</div>),
        "3"]];
    return (
        <Box
            sx={{
                mb: 0,
                p: 2,
                borderRadius: 1,
            }}
        >
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", fontSize: "1rem", mb: 1, }}>
                1. Executive Summary
            </Typography>
            <hr />
            <Typography variant="body2" sx={{ mb: 1, fontSize: "0.90rem" }}>
                The Security Testing Plan for the application outlines a structured approach to identify and mitigate potential security vulnerabilities that could compromise the application's integrity, confidentiality, and availability. This document provides an overview of the testing objectives, scope, methodologies, and deliverables to ensure the application adheres to the highest security standards.
            </Typography>

            <Typography variant="subtitle1" sx={{ fontWeight: "bold", fontSize: "0.95rem", mb: 1 }}>
                1.1 Overview: Project Background and Scope
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, fontSize: "0.90rem" }}>
                Security Testing team will identify the applications that would need security testing as a coordinated effort with {clientName} IT division and agree on the timelines and scope defined in this document.
            </Typography>

            <Typography variant="subtitle1" sx={{ fontWeight: "bold", fontSize: "0.95rem", mb: 1 }}>
                Applications in scope for Security testing:
            </Typography>
            <CustomTable headers={headers1} rows={rows1} />
            <CustomTable headers={headers2} rows={rows2} />

            <Typography variant="subtitle1" sx={{ fontWeight: "bold", fontSize: "0.95rem", mb: 1 }}>
                Applications out of scope are third-party integrations and applications mentioned below:
            </Typography>
            <CustomTable headers={headers3} rows={rows3} />

            <Typography variant="subtitle1" sx={{ fontWeight: "bold", fontSize: "1rem", mb: 1, }}>
                2. Security Test Planning and Strategy
            </Typography>
            <hr />
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", fontSize: "0.95rem", mb: 1 }}>
                2. 1 Static Application Security Testing (SAST) Approach
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", fontSize: "0.95rem", mb: 1 }}>
                2.1.1 Static Application Security Testing Methodology
            </Typography>
            <Typography variant="body2" sx={{ fontSize: "0.90rem" }}>
                Static analysis, commonly called “white box” testing, looks at applications in a non-runtime environment. This security testing method has distinct advantages in evaluating applications through advanced modelling. It can detect flaws in the software’s inputs and outputs that cannot be seen through dynamic web scanning alone.
            </Typography>
            <Box
                component="img"
                src={StaticAST}
                alt="Static Application Security Testing Phases"
                sx={{ width: "80%", display: "block", mx: "auto" }}
            />

            <Typography variant="body2" sx={{ mb: 1, fontSize: "0.90rem" }}>
                This consists of a comprehensive security analysis of a programming project's source code to discover bugs, security breaches, violations of programming conventions, or malicious backdoors.
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", fontSize: "0.95rem", mb: 1 }}>
                Phase 1- Prerequisites:
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, fontSize: "0.90rem" }}>
                <CustomBulletList>
                    <li>Before the start of the security assessment on the application, the security testing team must get the whole application code dump from the developers.</li>
                    <li>The entire source code provided for the security assessment should not contain any compilation errors.</li>
                    <li>Also, the source code should contain all the dependency files.</li>
                </CustomBulletList>
            </Typography>

            <Typography variant="subtitle1" sx={{ fontWeight: "bold", fontSize: "0.95rem", mb: 1 }}>
                Phase 2- Information Gathering:
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, fontSize: "0.90rem" }}>
                This phase is all about gathering enough information about the given code that is required for the vulnerability assessment. Information like
                <CustomBulletList>
                    <li>Identify the technologies/frameworks (JAVA, J2EE, C# etc.) involved to develop the application’s code base.</li>
                    <li>Check the total Lines of Code (LOC) of all projects involved in the application.</li>
                    <li>IDE (Integrated Development Environment) Tools.</li>
                    <li>Identify the security critical code components that would form the scope of security code review with the help of Client's Point of Contact (POC)</li>
                </CustomBulletList>
            </Typography>

            <Typography variant="subtitle1" sx={{ fontWeight: "bold", fontSize: "0.95rem", mb: 1 }}>
                Phase 3- Scanning:
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, fontSize: "0.90rem" }}>
                In this Phase we must plan entire security testing cycle based on the information of the application like the type of application and functionalities of the application, which also involves
                <CustomBulletList>
                    <li>Analyzing all the information gathered.</li>
                    <li>Understanding the process flow of the application.</li>
                    <li>Specifying test cases based on the available functionalities as per OWASP guidelines.</li>
                    <li>Identify the static code analysis tools that support the technologies involved in the application and perform automated static analysis to identify security vulnerabilities.</li>
                    <li>We acquire tools, either commercial or open source, based on the testing requirement and the availability of the license.</li>
                    <li>Feed the code to the code review tool like by uploading/supplying location of. Zip folders or binary files, pointing to the related codebase.</li>
                    <li>Configure the tool with the required scan settings/ policies to perform the scan.</li>
                    <li>Create rules to perform focused scans for standards or compliance.</li>
                    <li>Also, custom scan rules can be configured and added to the tool to uncover the related vulnerabilities.</li>
                    <li>Launch scan and monitor the scan progress, then analyze the report generated after the scan.</li>
                </CustomBulletList>
            </Typography>

            <Typography variant="subtitle1" sx={{ fontWeight: "bold", fontSize: "0.95rem", mb: 1 }}>
                Phase 4- Manual Review /False Positive Elimination:
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, fontSize: "0.90rem" }}>
                Once the scan is finished, the tool reports the vulnerabilities list, including false positives.. So, we must remove all the False Positives by manually checking the lines of code reported by the tool.
                <CustomBulletList>
                    <li>Manual review of the results presented by the tool</li>

                </CustomBulletList>
            </Typography>

            <Typography variant="subtitle1" sx={{ fontWeight: "bold", fontSize: "0.95rem", mb: 1 }}>
                Phase 5: Reporting:
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, fontSize: "0.90rem" }}>
                <CustomBulletList>
                    <li>This phase is about documenting all the identified vulnerabilities after filtering the false positives from the report generated by the tool.</li>
                    <li>When a report is created manually, the report must have all the below parameters with details.
                        <ul>
                            <li>Vulnerability name</li>
                            <li>Vulnerability Description</li>
                            <li>Code location/Line No. or Point of Impact</li>
                            <li>Impact of Vulnerability</li>
                            <li>Countermeasures or Remediation</li>
                        </ul>
                    </li>
                </CustomBulletList>
            </Typography>

            <Typography variant="subtitle1" sx={{ fontWeight: "bold", fontSize: "0.95rem", mb: 1 }}>
                2.1.2 Tests in line with OWASP Top 10 2021 will be performed
            </Typography>
            <CustomTable headers={headers4} rows={rows4} />

            <Typography variant="subtitle1" sx={{ fontWeight: "bold", fontSize: "0.95rem", mb: 1 }}>
                2.1.3 Static Application Security Checklist
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, fontSize: "0.90rem" }}>
                <CustomBulletList>
                    <li>What security vulnerabilities are susceptible to?</li>
                    <li>Are authorization and authentication managed in the right way?</li>
                    <li>Is user input validated, sanitized, and escaped to prevent security attacks such as cross-site scripting, SQL injection, and Buffer overflow?</li>
                    <li>Is sensitive data like user data, credit card information securely managed and stored?</li>
                    <li>Does this code change reveal some secret information like keys, passwords, or usernames?</li>
                    <li>Is data retrieved from external APIs or libraries checked accordingly?</li>
                    <li>Does error handling or logging expose us to vulnerabilities?</li>
                    <li>Is the right encryption used?</li>
                </CustomBulletList>
            </Typography>

            <Typography variant="subtitle1" sx={{ fontWeight: "bold", fontSize: "0.95rem", mb: 1 }}>
                2.1.3 Test Cases
            </Typography>
            {securityTestPlan?.sastTestCases && (
                <CustomTable headers={sastTestCaseHeaders} rows={sastTestCaseRows} />
            )}

            <Typography variant="subtitle1" sx={{ fontWeight: "bold", fontSize: "0.95rem", mb: 1 }}>
                2.2 Dynamic Application Security Testing (DAST) Approach
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", fontSize: "0.95rem", mb: 1 }}>
                2.2.1 DAST – Dynamic Application Security Testing
            </Typography>
            <Typography variant="body2" sx={{ fontSize: "0.90rem" }}>
                Dynamic analysis is a non-functional assessment performed to identify vulnerabilities in an application. These vulnerabilities, when exploited by a malicious user (attacker/hacker), may compromise the confidentiality, integrity, or availability of the application and/or the information it processes or distributes. Some common vulnerabilities affecting web applications include SQL Injection (Structured Query Language Injection), XSS (Cross-Site Scripting), CSRF (Cross-Site Request Forgery), Remote File Inclusion, and others.
                <br />This testing enables companies to identify and remediate security issues in their running applications before hackers can exploit them. By dynamically testing applications at runtime, the service evaluates them in the same way a hacker would attack, providing accurate and actionable vulnerability detection.
                <br />Typical phases of the application penetration testing are depicted in the figure below:
            </Typography>
            <Box
                component="img"
                src={DynamicAST}
                alt="Static Application Security Testing Phases"
                sx={{ width: "80%", display: "block", mx: "auto" }}
            />

            <Typography variant="subtitle1" sx={{ fontWeight: "bold", fontSize: "0.95rem", mb: 1 }}>
                Phase 1 & 2 – Information Gathering, Planning and Analysis
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, fontSize: "0.90rem" }}>
                <CustomBulletList>
                    <li>Determine the components in the application and its underlying environment.</li>
                    <li>Understand the security objectives of the application.</li>
                    <li>Navigate through the application and carry out threat modeling to identify possible threats and vulnerabilities.</li>
                    <li>Confirm that the application and its components are suitable for testing.
                        Sample/default web pages, if found, shall be excluded from assessment.</li>
                </CustomBulletList>
            </Typography>

            <Typography variant="subtitle1" sx={{ fontWeight: "bold", fontSize: "0.95rem", mb: 1 }}>
                Phase 3 – Vulnerability Detection
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, fontSize: "0.90rem" }}>
                <CustomBulletList>
                    <li>Identification of vulnerabilities related to underlying architecture.</li>
                    <li>Identification of OS, web server, and database server, along with their versions (if possible).</li>
                    <li>Identification of known threats associated with those versions.</li>
                    <li>Identification of open ports and services running on those ports.</li>
                    <li>Identification of security risks/threats associated with the open port and the service running on it.</li>
                    <li>Ensure the web server does not allow PUT/DELETE methods for resource manipulation.</li>
                </CustomBulletList>
            </Typography>

            <Typography variant="subtitle1" sx={{ fontWeight: "bold", fontSize: "0.95rem", mb: 1 }}>
                Phase 4 – Penetration Testing & Attack(s)
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, fontSize: "0.90rem" }}>
                <CustomBulletList>
                    <li>Identification of all the web pages with input controls.</li>
                    <li>Perusal of client-side code for disclosure of potentially compromising information.</li>
                    <li>Tests (both manual and automated) are carried out to identify vulnerabilities like HTTP Response Splitting, SQL Injection, Cross-Site Scripting (XSS), Directory Browsing/Traversal, AJAX Testing, and Parameter Tampering.</li>
                    <li>Checks for the presence of vulnerabilities related to the latest breed of technologies.</li>
                    <li>Checks for the presence of encryption. Ensure that usernames and passwords are sent over an encrypted channel.</li>
                    <li>Detect code information leaks through invalid input responses and unintentional disclosure (error messages, etc.).</li>
                    <li>Checks to see if it is possible to access pages or functions that require a logon but can be bypassed.</li>
                    <li>Determine the application logic to maintain the authentication sessions - number of (consecutive) failure logins allowed.</li>
                    <li>Ensure that where the application requires the user to perform actions in a specific sequence, the sequence is enforced.</li>
                    <li>Ensure that users are only asked to submit authentication credentials on pages that are served with SSL (if applicable).</li>
                    <li>Ensure that no backup files of source code are accessible on the publicly accessible part of the application.</li>
                    <li>Ensure that common configuration issues, such as directory listings and sample files, have been addressed.</li>
                    <li>Check for the existence of common directories within the application root.</li>
                    <li>Ensure the applications will not process operating system commands from the user.</li>
                </CustomBulletList>
            </Typography>

            <Typography variant="subtitle1" sx={{ fontWeight: "bold", fontSize: "0.95rem", mb: 1 }}>
                Phase 5 – Reports & Deliverables
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, fontSize: "0.90rem" }}>
                <CustomBulletList>
                    <li>Compile all the security findings in an easy-to-understand format and rate them based on their criticality level.</li>
                    <li>Determine Risk, Technical Impact, and Business Impact based on the vulnerabilities detected</li>
                    <li>Implications of the findings will be explained in detail.l</li>
                    <li>All the findings are supported by the relevant screenshots and videos (wherever possible).</li>
                </CustomBulletList>
            </Typography>

            <Typography variant="subtitle1" sx={{ fontWeight: "bold", fontSize: "0.95rem", mb: 1 }}>
                2.2.2 Testing Standards Adopted
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, fontSize: "0.90rem" }}>
                Tests in line with OWASP Top-10 2021 will be executed
            </Typography>
            <CustomTable headers={headers4} rows={rows4} />

            <Typography variant="subtitle1" sx={{ fontWeight: "bold", fontSize: "0.95rem", mb: 1 }}>
                2.2.3 Dynamic Application Security Test Cases
            </Typography>

             {securityTestPlan?.dastTestCases && (
                <CustomTable headers={dastTestCaseHeaders} rows={dastTestCaseRows} />
            )}
            

            <Typography variant="subtitle1" sx={{ fontWeight: "bold", fontSize: "0.95rem", mb: 1 }}>
                3. Security Test Execution
            </Typography>
            <hr />
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", fontSize: "0.95rem", mb: 2 }}>
                3.1 Security Testing Schedule
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", fontSize: "0.95rem", mb: 1 }}>
                The table below provides a high-level schedule of SAST.
            </Typography>
            {securityTestPlan?.securityTestPlan?.SASTSchedule && (
                <CustomTable headers={sastHeaders} rows={sastRows} />
            )}


            <Typography variant="subtitle1" sx={{ fontWeight: "bold", fontSize: "0.95rem", mb: 1 }}>
                The table below provides a high-level schedule of DAST/VAPT.
            </Typography>
            <CustomTable headers={dastHeaders} rows={dastRows} />
            <Typography variant="subtitle1" sx={{ fontWeight: "bold", fontSize: "0.95rem", mb: 0 }}>
                Key Deliverables
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, fontSize: "0.90rem" }}>
                <CustomBulletList>
                    <li>Security Testing Reports</li>
                    <li>Vulnerability Scan Findings</li>
                    <li>POC’s for the findings (Vulnerabilities)</li>
                    <li>Security Testing Metrics</li>
                    <ul>
                        <li>Vulnerability Summary</li>
                        <li>Technical Summary</li>
                        <li>Risk Rating</li>
                        <li>Vulnerability Distribution</li>
                        <li>CIA Impact</li>
                        <li>Security Posture</li>
                        <li>Unexpected Services</li>
                        <li>ROI (Return On Investment) - The Test cycle should be more than one</li>
                        <li>Risk Exposure Reduction - The Test cycle should be more than one</li>
                    </ul>
                </CustomBulletList>
            </Typography>

            <Typography variant="subtitle1" sx={{ fontWeight: "bold", fontSize: "0.95rem", mb: 1 }}>
                3.2 Security Test Environment
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, fontSize: "0.90rem" }}>
                Pre-production or Staging environment should be provided for security testing, and this environment should not be shared with any other testing activity.
            </Typography>

            <Typography variant="subtitle1" sx={{ fontWeight: "bold", fontSize: "0.95rem", mb: 1 }}>
                3.3 Assumptions and Dependencies
            </Typography>

            <Typography variant="subtitle1" sx={{ fontWeight: "bold", fontSize: "0.90rem", mb: 1 }}>
                3.3.1 Assumptions
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, fontSize: "0.90rem" }}>
                Assumptions should be documented concerning the available release software, test environment, dependencies, tools, and test schedule associated with the Security Testing. Examples are shown below:
                <CustomBulletList>
                    <li>Cybersecurity will adhere to industry best practices and make all reasonable efforts to identify security vulnerabilities while carrying out the defined scope of services.</li>
                    <li>Applications will be assessed based on current threats, exploits, and vulnerabilities at the time of the test. Any vulnerabilities emerging after the testing process concludes will fall outside the scope of this exercise.</li>
                    <li>It is assumed that no changes will be made to the application, network, or underlying IT infrastructure during the assessment. Any changes made during this period will invalidate the findings and will require a separate engagement for reassessment.</li>
                    <li> {clientName} will appoint a single point of contact to act as the primary interface and escalation point for the Cybersecurity team.</li>
                    <li>Additionally, {clientName} may need to make certain technical staff available, possessing the necessary knowledge and expertise to collaborate with the Cybersecurity team in resolving project issues. These personnel shall be available as required to ensure timely issue resolution and completion of the assessment on schedule.</li>
                    <li>Regression testing will involve retesting only the vulnerabilities identified during the initial assessment at the specific instances/locations where they were found. Identifying new vulnerabilities beyond those discovered during the initial test is not within the scope of regression testing.</li>
                </CustomBulletList>
            </Typography>

            <Typography variant="subtitle1" sx={{ fontWeight: "bold", fontSize: "0.90rem", mb: 1 }}>
                3.3.2 Dependencies
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, fontSize: "0.90rem" }}>
                <CustomBulletList>
                    <li>{clientName} shall make available the necessary access information for all applications in scope for the security assessment.</li>
                    <li>Cybersecurity will follow standard practices and procedures to ensure secure use of this environment and shall not be liable for any security incidents resulting from issues outside its control.</li>
                    <li>{clientName} shall provide at least two user credentials for each user role (if any) to facilitate thorough testing across all functionalities.</li>
                    <li>{clientName} to provide scan targets that are in-scope.</li>
                    <li>If required, {clientName} shall obtain all necessary approvals from the cloud service provider to permit scanning of the hosts and web applications deployed on the cloud infrastructure.</li>
                </CustomBulletList>
            </Typography>

        </Box >
    );
};

export default SecurityTestPlan;
